import type { GridColDef } from '@mui/x-data-grid-pro'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import { base_colorContentBorder } from 'ext-ui/style/colors'
import React, { ReactElement, useCallback, useMemo } from 'react'
import DataGrid, {
  DataGridHeaderStyle,
  DataGridRowHeight,
} from 'src/components/DataGrid/DataGrid'
import { useTransactionsColumns } from 'src/components/TransactionsDataGrid/transactionsDataGrid.columns'
import type { TransactionsDataGridRowModel } from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
// Shared with the stats roll-up so the tab has ONE navigation idiom and the
// modifier-click guard (⌘/Ctrl/Shift/Alt, non-primary button) lives in one
// place. TODO: promote out of CardOverviewStats if a third section needs it.
import StatNavLink from 'src/feature/CardOverviewStats/StatNavLink'
import { useTranslate } from 'src/localization/Trns'
import { TransactionStatus } from 'src/models/transaction.models'
import { useQueryTransactions } from 'src/queries/transactions.queries'

import * as style from './CardOverviewRecentTransactions.style'

/** The design shows five rows; the ticket caps it at five. */
export const RECENT_TRANSACTIONS_LIMIT = 5

/**
 * The six columns the Overview panel shows, in design order.
 *
 * Deliberately NOT expressed as a `ColConfig` entry in
 * `transactionsDataGrid.config.ts`, for two reasons:
 *
 *  1. That config cannot drop expense-category columns, which this panel must.
 *     `exclude` matches exact field names but EC fields are minted per category
 *     as `ec:<uuid>`, and omitting `EC_COLUMN_BLOCK` from `order` doesn't drop
 *     them either — `getConfiguredColumns` step 5 re-appends orphaned ECs. So an
 *     allowlist is required regardless, and one allowlist beats a config entry
 *     plus an allowlist.
 *  2. Registering a new `TransactionReportType` would also opt this panel into
 *     the filter-schema defaults and the saved-view namespace keyed off that
 *     enum — three subsystems a read-only summary doesn't use, and all four
 *     switches on it are non-exhaustive, so it would inherit them silently.
 *
 * The expense-category ("Account") column is out of scope until there is a
 * defined display rule for orgs with more than one category type — EX-40633.
 */
const OVERVIEW_COLUMN_FIELDS = [
  'date',
  'merchant',
  'cardUser',
  'card',
  'receipts',
  'amount',
] as const

/**
 * Distinct from the Transactions tab's list query so `useQueryFetchedTransactions`
 * — which reads the FIRST cached transactions-list key and warns when it finds
 * more than one — can tell them apart.
 */
const LOCATION_KEY = 'cardOverviewRecentTransactions'

/**
 * The status set the transactions report expects when the caller supplies none.
 *
 * Module scope, and referentially stable, because it feeds a `useMemo`'d filters
 * object that is itself a query key — an inline array would mint a new key every
 * render and refetch in a loop.
 */
const TRANSACTION_STATUSES = [
  TransactionStatus.Pending,
  TransactionStatus.Cleared,
  TransactionStatus.Declined,
  TransactionStatus.AuthReversal,
]

/** `DataGrid` hard-codes `columnHeaderHeight={44}`. */
const HEADER_HEIGHT = 44
/**
 * `DataGrid` renders its grid at `height - 60`, an inset sized for the full-page
 * tables it was built for (toolbar + footer). This panel has neither — the
 * footer is hidden below — so the inset is added back rather than left as dead
 * space under the last row.
 */
const DATA_GRID_HEIGHT_INSET = 60

type CardOverviewRecentTransactionsProps = {
  className?: string
  creditCardId: string
  /**
   * Destination, built by the mounting page — features do not know URLs.
   *
   * `undefined` when the card has no Transactions tab to link to (a pending
   * card, for instance). The panel still renders its list: the transactions are
   * real and worth showing at any card state, and it is only the navigation
   * that has nowhere to go. The heading renders without the link.
   */
  viewAllHref?: string
  /** SPA navigation, supplied by the page. See `StatNavLink`. */
  onNavigate: (href: string) => void
  /**
   * `isSplit` rides along because selecting a split transaction must evict its
   * cached detail before navigating (the full grid does the same). Deciding what
   * selection *means* — evict, navigate — belongs to the page; this component
   * only reports that it happened.
   */
  onSelectTransaction: (transactionId: string, isSplit: boolean) => void
}

/**
 * The five most recent transactions for a credit card, for the redesigned
 * card-detail Overview tab (EX-40633).
 *
 * Reuses the Transactions tab's column factory rather than re-implementing
 * cells, so the date / merchant / card / receipt renderers stay in step with the
 * full grid. Routing-free by the `src/feature/**` boundary: the page supplies the
 * "view all" destination and the row-selection handler.
 */
const CardOverviewRecentTransactions = ({
  className,
  creditCardId,
  viewAllHref,
  onNavigate,
  onSelectTransaction,
}: CardOverviewRecentTransactionsProps): ReactElement => {
  const translate = useTranslate()

  const filters = useMemo(
    () => ({
      creditCardId: [creditCardId],
      sort: '-date',
      // REQUIRED, not a refinement. The server does not apply a default status
      // set: given none, it returns rows with the billing amounts unprojected,
      // so every Amount cell falls through to `clearingOrAuthLabel`'s
      // `<EmptyLabel />` and the column reads as a wall of dashes.
      //
      // This panel bypasses the shared filter schema (see the column note
      // below), and the schema is where that contract normally gets honoured —
      // `finalizeApi` in transactionsFilter.schema.ts injects exactly this list
      // when the caller supplies none, under the comment "server expects a
      // default list if not provided". `VirtualCardDetail` hardcodes the same
      // four for the same reason. Restated here rather than imported because
      // the schema's copy is buried in a mapper this file cannot call.
      status: TRANSACTION_STATUSES,
    }),
    [creditCardId],
  )

  const { data, isPending, isError } = useQueryTransactions({
    filters,
    perPage: RECENT_TRANSACTIONS_LIMIT,
    locationKey: LOCATION_KEY,
    // Overrides the list factory's `placeholderData: keepPreviousData`, which is
    // right for the full Transactions tab and wrong here. There, filters change
    // under a stable heading and a flash to empty is worse than briefly stale
    // rows. Here the heading IS the card: `AccountEntityPage` renders this from
    // a keyless <Route>, so moving between two card overviews re-renders rather
    // than remounts, and the previous card's merchants and amounts would paint
    // under the new card's heading with `isPending` already false.
    placeholderData: undefined,
  })

  // No date window: "five most recent" should not go blank on a quiet card.
  // Sliced as well as paged because `perPage` bounds the request, not the
  // flattened infinite-query result.
  const transactions = useMemo(
    () => (data?.transactions ?? []).slice(0, RECENT_TRANSACTIONS_LIMIT),
    [data?.transactions],
  )

  const rows = useMemo(
    () =>
      transactions.map((transaction, index) => ({
        id: transaction.id,
        transaction,
        index,
        // The row model carries precomputed expense-category cell data for the
        // full grid's `ec:*` columns. This panel renders none, so it is empty.
        expenseCategoryCellData: {},
      })),
    [transactions],
  )

  // No `context`: `pickConfig` falls back to the `default` ColConfig, which
  // returns every column. The allowlist below picks the six this panel shows.
  const { columns: allColumns } = useTransactionsColumns({ filters: {} })

  /**
   * Supplies the header element instead of letting MUI render its own, so this
   * panel's headers match the sibling `CardBudgetsTable` — the uppercase kicker
   * over a hairline that the design calls for, not the shared grid's filled
   * band.
   *
   * MUI wraps its own title in an emotion-styled element, so restyling that from
   * outside is a cascade race we lose intermittently. `headerName` stays set on
   * every column, because that is what MUI reads for the accessible name — this
   * only replaces the rendering.
   */
  const renderHeader = useCallback(
    (label: string, align?: 'right') => (): ReactElement => (
      <span
        css={align === 'right' ? style.columnHeaderRight : style.columnHeader}
      >
        {label}
      </span>
    ),
    [],
  )

  const columns = useMemo(
    () =>
      OVERVIEW_COLUMN_FIELDS.map((field) =>
        allColumns.find((column) => String(column.field) === field),
      )
        .filter(
          (column): column is GridColDef<TransactionsDataGridRowModel> =>
            column !== undefined,
        )
        // Read-only summary: the full grid makes expense-category and some other
        // cells click-to-edit, which would fire mutations from a panel that has
        // no way to surface their result.
        //
        // `sortable` goes the same way, and needs to be forced rather than
        // inherited: `receipts` is the one column the factory leaves sortable
        // (behind `receiptVerificationUiEnabled`), the shared grid pins
        // `sortingMode="server"`, and this panel passes no `onSortModelChange`.
        // Left alone, that header offers an arrow that reorders nothing —
        // `TransactionsDataGrid` gets away with it only because it owns a
        // `sortModel` and re-sorts client-side, none of which came along here.
        .map((column) => {
          /*
            Amount is the design's terminal column, right-aligned over its
            values. `align`/`headerAlign` rather than CSS so MUI keeps the cell
            and its header in step — and because `DataGridCell` reads
            `colDef.align` itself to right-align its text container, which is
            what actually moves the factory's own cell content.

            This is a deliberate divergence from the full Transactions tab,
            which still left-aligns. The tab tables are a later story; see the
            note on `OVERVIEW_COLUMN_FIELDS`.
          */
          const isAmount = String(column.field) === 'amount'
          return {
            ...column,
            editable: false,
            sortable: false,
            ...(isAmount
              ? { align: 'right' as const, headerAlign: 'right' as const }
              : {}),
            // `headerName` is left untouched above — MUI still reads it for the
            // accessible name; this only swaps the rendered element.
            renderHeader: renderHeader(
              column.headerName ?? '',
              isAmount ? 'right' : undefined,
            ),
          }
        }),
    [allColumns, renderHeader],
  )

  const selectRow = useCallback(
    (row: TransactionsDataGridRowModel): void => {
      onSelectTransaction(row.transaction.id, !!row.transaction.isSplit)
    },
    [onSelectTransaction],
  )

  const isEmpty = !isPending && !isError && rows.length === 0

  const body = (): ReactElement => {
    if (isPending) {
      return (
        <p css={style.statusBody} role="status">
          {translate('Loading transactions')}
        </p>
      )
    }

    if (isError) {
      return (
        <p css={style.statusBody} role="alert">
          {translate("We couldn't load recent transactions. Try again later.")}
        </p>
      )
    }

    if (isEmpty) {
      return (
        <p css={style.statusBody}>
          {translate('No transactions on this card yet.')}
        </p>
      )
    }

    return (
      <DataGrid<typeof filters, TransactionsDataGridRowModel>
        css={style.grid}
        rows={rows}
        columns={columns}
        filtersBody={filters}
        rowHeight={DataGridRowHeight.Default}
        // The design's header is a plain uppercase kicker over a hairline, not
        // the shared grid's filled band — the same treatment the sibling
        // `CardBudgetsTable` uses, so the two panels read as one surface.
        headerStyle={DataGridHeaderStyle.NoBackground}
        height={
          HEADER_HEIGHT +
          rows.length * DataGridRowHeight.Default +
          DATA_GRID_HEIGHT_INSET
        }
        // Fixed column set, and all of it has to be said explicitly —
        // DataGridPro ships drag-reorder and resize on by default, so the
        // previous `disableColumnHiding` alone left two of the three live.
        //
        // `disableColumnMenu` rather than `disableColumnHiding`: it subsumes it,
        // and it is the only one that removes the ⋮ entirely. Hiding-only leaves
        // a menu holding just `GridColumnMenuSortItem`, which renders `null` for
        // a non-sortable column — so with every column forced `sortable: false`
        // above, the affordance would open an empty popover.
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        pinFirstColumn={false}
        checkboxSelection={false}
        // A five-row panel has no pagination and no selection, so MUI's default
        // footer contributes a "Total Rows: 5" line and ~52px of chrome that
        // says nothing the heading doesn't.
        hideFooter
        // Five rows never need windowing, and virtualization is what makes the
        // scroller's measured height wobble while it settles — which is what
        // makes page scroll catch on the table. Off, the rows are plain DOM.
        disableVirtualization
        sx={{
          // No resizing and no reordering here, so the grab handles the shared
          // grid keeps at `opacity: 1` have nothing to grab — they only read as
          // column dividers, which the design does not have.
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
          // One hairline under the whole header row; the shared grid marks the
          // band with a background fill instead, which NoBackground removes.
          //
          // Header padding is deliberately NOT zeroed the way `CardBudgetsTable`
          // does it. That table renders its cells flush, so its headers had to
          // come in to meet them; these cells carry `DataGridCellWrapper`'s own
          // 16px inset, which is already what the shared grid pads headers by.
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: `1px solid ${base_colorContentBorder}`,
          },
          // The scrollbar gutter's filler cell carries a left border, which
          // reads as a stray rule floating past the last column. Same fix as
          // `CardBudgetsTable` (EX-40737); this panel scrolls horizontally, so
          // it is the more visible of the two.
          '& .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-filler': {
            borderLeft: 'none',
          },
          // ⚠️ `DataGrid` merges consumer `sx` with a SHALLOW spread, so this
          // key REPLACES its `.MuiDataGrid-virtualScroller` rule rather than
          // extending it. Every property that rule sets has to be restated
          // here or it is silently dropped — `overscrollBehaviorX: 'none'`
          // most consequentially, because losing it lets a horizontal trackpad
          // swipe past the panel's left edge trigger browser BACK navigation.
          // This panel does scroll horizontally: its six columns' minWidths sum
          // to roughly 840px.
          //
          // Only `overscrollBehaviorY` and `overflowY` below are this panel's
          // own; the rest are the shared grid's, carried over verbatim.
          '& .MuiDataGrid-virtualScroller': {
            willChange: 'transform',
            transform: 'translateZ(0)',
            contain: 'layout paint',
            backfaceVisibility: 'hidden',
            overscrollBehaviorX: 'none',
            // The shared grid sets `contain` so a TALL, genuinely scrollable
            // table doesn't scroll the page out from under it. This panel is
            // never vertically scrollable, so that only gets in the way: any
            // sub-pixel scrollable range makes the scroller swallow wheel
            // events instead of letting the page take them.
            //
            // Note this does NOT restore native scroll chaining. `DataGrid`
            // still attaches `useTransferVerticalScroll`'s `{passive: false}`
            // wheel listener, and with `overflowY: 'hidden'` forced here its
            // `canConsumeVerticalScroll` is always false — so every wheel event
            // over the panel is preventDefault()ed and the ancestor is scrolled
            // from JS. It scrolls correctly, but without native momentum.
            // Removing that listener is the shared grid's call, not this
            // panel's.
            overscrollBehaviorY: 'auto',
            overflowY: 'hidden',
          },
        }}
        onRowClick={(params, event): void => {
          // A cell that handled the click itself (a link, a tooltip trigger)
          // preventDefaults; don't navigate out from under it.
          if (event.defaultPrevented) return
          selectRow(params.row)
        }}
        // Row navigation must not be mouse-only (src/CLAUDE.md WCAG AA floor).
        // The full Transactions grid gets away with click-only because its
        // chevron column is a bare SVG, not a link — so there is nothing to
        // inherit here. MUI gives cells a roving tabindex, so Enter on the
        // focused cell is the keyboard equivalent of clicking its row. Safe
        // against edit mode because every column above is `editable: false`.
        onCellKeyDown={(params, event): void => {
          if (event.key !== 'Enter' || event.defaultPrevented) return
          event.defaultMuiPrevented = true
          selectRow(params.row)
        }}
      />
    )
  }

  return (
    <ContentCard
      className={className}
      css={style.panel}
      data-testid="cardOverviewRecentTransactions"
    >
      <div css={style.header}>
        <h2 css={style.title}>{translate('Recent Transactions')}</h2>
        {viewAllHref !== undefined && (
          <StatNavLink href={viewAllHref} onNavigate={onNavigate}>
            {/* Arrow in the source string, matching every sibling link on the
                tab ("View budgets →", "Adjust limit →"). */}
            {translate('View all transactions →')}
          </StatNavLink>
        )}
      </div>
      {body()}
    </ContentCard>
  )
}

export default CardOverviewRecentTransactions
